import { useState, useMemo } from 'react';
import { Loader2, ChevronDown, Check } from 'lucide-react';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { TabNav } from '@g17eco/ui-modern/components/ui/tab-nav';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';
import { IntensityTreemap } from './IntensityTreemap';
import { EmptyStateCard } from '../EmptyStateCard';
import { AssetTypeFilter, type AssetFilterValue } from './AssetTypeFilter';
import { getDisabledAssetTypes } from './assetFilterUtils';
import {
  buildTreemapData,
  getAvailableMetrics,
  type TreemapNode,
} from './intensityTreemapUtils';
import { useGetIntensityTreemapQuery, useGetAssetsQuery, useGetAssetComparisonQuery } from '../../api/emissions';
import { useAppSelector } from '@reducers/index';
import { getCurrentInitiativeTree } from '@selectors/initiative';
import { ASSET_TEMPLATE_CONFIGS, isBulkImportableAssetType } from '@g17eco/emissions-config';
import { AssetType, type EmissionAsset, type IntensityTreemapItem } from '../../types/emissions';
import { INTENSITY_FIELD_META, type IntensityFieldMeta } from '../../config/intensityFieldBranding';

type EntityView = 'reporting-levels' | 'assets';

// ============================================================================
// Asset-specific metric definitions — derived from ASSET_TEMPLATE_CONFIGS
// ============================================================================

interface AssetMetricDef {
  key: string;
  label: string;
  unit: string;
  denominatorLabel: string;
  getDenominator: (asset: Record<string, unknown>) => number | null;
}


const numericField = (key: string, minValue = 0) => (a: Record<string, unknown>): number | null =>
  typeof a[key] === 'number' && (a[key] as number) > minValue ? (a[key] as number) : null;

const getFieldMeta = (key: string): IntensityFieldMeta | undefined =>
  (INTENSITY_FIELD_META as Record<string, IntensityFieldMeta>)[key];

/**
 * Per-asset-type metric keys not surfaced via the XLSX template config but
 * still meaningful as intensity denominators. Today: `unitCount` on Vehicle
 * (a Vehicle group of N units → kgCO₂e per unit). Kept separate from the
 * template config so the bulk-import sheet stays unchanged.
 */
const EXTRA_ASSET_METRIC_KEYS: Partial<Record<AssetType, string[]>> = {
  [AssetType.Vehicle]: ['unitCount'],
};

const buildMetric = (key: string): AssetMetricDef | null => {
  const meta = getFieldMeta(key);
  if (!meta) return null;
  return { key, ...meta, getDenominator: numericField(key, meta.minValue ?? 0) };
};

/** Build standard metric defs for an asset type from its template config */
const getAssetTypeMetrics = (assetType: AssetType): AssetMetricDef[] => {
  const metrics: AssetMetricDef[] = [];
  if (isBulkImportableAssetType(assetType)) {
    const config = ASSET_TEMPLATE_CONFIGS[assetType];
    for (const field of config.assetFields) {
      if (field.key.startsWith('customIntensity')) continue;
      const metric = buildMetric(field.key);
      if (metric) metrics.push(metric);
    }
  }
  for (const key of EXTRA_ASSET_METRIC_KEYS[assetType] ?? []) {
    const metric = buildMetric(key);
    if (metric) metrics.push(metric);
  }
  return metrics;
};

/** Collect all standard metrics across every asset type (deduped by key) */
const ALL_STANDARD_METRICS: AssetMetricDef[] = (() => {
  const seen = new Set<string>();
  const result: AssetMetricDef[] = [];
  const push = (key: string) => {
    if (seen.has(key)) return;
    const metric = buildMetric(key);
    if (!metric) return;
    seen.add(key);
    result.push(metric);
  };
  for (const config of Object.values(ASSET_TEMPLATE_CONFIGS)) {
    for (const field of config.assetFields) {
      if (field.key.startsWith('customIntensity')) continue;
      push(field.key);
    }
  }
  for (const keys of Object.values(EXTRA_ASSET_METRIC_KEYS)) {
    for (const key of keys ?? []) push(key);
  }
  return result;
})();

/** Discover custom intensity metrics from actual asset data */
const getCustomMetrics = (assets: EmissionAsset[]): AssetMetricDef[] => {
  const labels = new Set<string>();
  for (const asset of assets) {
    if (asset.customIntensityLabel && asset.customIntensityValue && asset.customIntensityValue > 0) {
      labels.add(asset.customIntensityLabel);
    }
  }
  return [...labels].map((label) => ({
    key: `custom:${label}`,
    label,
    unit: `kgCO₂e/${label}`,
    denominatorLabel: label,
    getDenominator: (a: Record<string, unknown>) =>
      a.customIntensityLabel === label && typeof a.customIntensityValue === 'number' && a.customIntensityValue > 0
        ? a.customIntensityValue
        : null,
  }));
};


// ============================================================================
// Panel Props
// ============================================================================

interface IntensityTreemapPanelProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
  entityView: EntityView;
}

export const IntensityTreemapPanel = ({ initiativeId, periodStart, periodEnd, includeChildren, entityView }: IntensityTreemapPanelProps) => {
  if (entityView === 'assets') {
    return <AssetTreemapView initiativeId={initiativeId} periodStart={periodStart} periodEnd={periodEnd} includeChildren={includeChildren} />;
  }
  return <ReportingLevelTreemapView initiativeId={initiativeId} periodStart={periodStart} periodEnd={periodEnd} />;
};

// ============================================================================
// Reporting Level View
// ============================================================================

const ReportingLevelTreemapView = ({ initiativeId, periodStart, periodEnd }: { initiativeId: string; periodStart: string; periodEnd: string }) => {
  const { data, isLoading } = useGetIntensityTreemapQuery(
    { initiativeId, periodStart, periodEnd },
    { skip: !initiativeId }
  );
  const allChildren = useAppSelector(getCurrentInitiativeTree);

  // Merge API data with full initiative tree so all children appear
  const items: IntensityTreemapItem[] = useMemo(() => {
    const apiItems = data?.initiatives ?? [];
    const children = allChildren;
    if (children.length === 0) return apiItems;

    const apiMap = new Map(apiItems.map((item) => [item._id, item]));
    return children.map((child) => {
      const existing = apiMap.get(child._id);
      if (existing) return existing;
      return {
        _id: child._id,
        name: child.name,
        code: child.code,
        totalCo2e: 0,
        totalActivities: 0,
        businessMetrics: null,
      };
    });
  }, [data?.initiatives, allChildren]);

  const availableMetrics = useMemo(() => getAvailableMetrics(items), [items]);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const activeMetric = availableMetrics.find((m) => m.key === selectedMetric) ?? availableMetrics[0] ?? null;

  const treemapData = useMemo(() => {
    if (!activeMetric) return [];
    return buildTreemapData(items, activeMetric.key);
  }, [items, activeMetric]);

  const excludedCount = items.length - treemapData.length;

  if (isLoading) {
    return <div className='tw:flex tw:items-center tw:justify-center tw:py-10'><Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' /></div>;
  }

  if (items.length === 0) {
    return <EmptyStateCard title='Intensity Treemap' message='No reporting levels found.' />;
  }

  if (!activeMetric) {
    return <EmptyStateCard title='Intensity Treemap' message='No reporting levels have business metrics (employees, revenue, or floor area) configured for this period.' />;
  }

  return (
    <div className='tw:space-y-3'>
      <IntensityTreemap
        data={treemapData}
        metric={activeMetric}
        headerExtra={<MetricSelector standardMetrics={availableMetrics} customMetrics={[]} value={activeMetric.key} onChange={setSelectedMetric} />}
      />
      {excludedCount > 0 && (
        <p className='tw:text-xs tw:text-muted-foreground'>
          Showing {treemapData.length} of {items.length} reporting levels. {excludedCount} excluded (missing {activeMetric.label} data).
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Asset View — shows ALL assets with asset type filter
// ============================================================================

const AssetTreemapView = ({ initiativeId, periodStart, periodEnd, includeChildren }: { initiativeId: string; periodStart: string; periodEnd: string; includeChildren?: boolean }) => {
  const { data: assetsResponse, isLoading: assetsLoading } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren });
  const { data: comparisonData, isLoading: comparisonLoading } = useGetAssetComparisonQuery(
    { initiativeId, periodStart, periodEnd, includeChildren },
    { skip: !initiativeId }
  );
  const isLoading = assetsLoading || comparisonLoading;

  const assets = useMemo(() => assetsResponse?.data ?? [], [assetsResponse?.data]);
  const comparisonAssets = useMemo(() => comparisonData?.assets ?? [], [comparisonData?.assets]);
  const comparisonMap = useMemo(() => new Map(comparisonAssets.map((a) => [a._id, a])), [comparisonAssets]);
  const disabledTypes = useMemo(() => getDisabledAssetTypes(assets), [assets]);

  // Asset type filter
  const [selectedAssetType, setSelectedAssetType] = useState<AssetFilterValue>('all');
  const [selectedMetricKey, setSelectedMetricKey] = useState<string | null>(null);

  // Build available metrics: config-defined standard metrics + custom metrics from data
  const { standardMetrics, customMetrics } = useMemo(() => {
    const filteredAssets = selectedAssetType === 'all' || selectedAssetType === 'unassigned'
      ? assets
      : assets.filter((a) => a.assetType === selectedAssetType);

    // For a specific asset type, show ALL its config-defined metrics (even if no data yet).
    // For "all", only show metrics that have at least one asset with data.
    const standard = selectedAssetType === 'all' || selectedAssetType === 'unassigned'
      ? ALL_STANDARD_METRICS.filter((metric) =>
          filteredAssets.some((asset) => metric.getDenominator(asset as unknown as Record<string, unknown>) != null)
        )
      : isBulkImportableAssetType(selectedAssetType) ? getAssetTypeMetrics(selectedAssetType as AssetType) : [];

    return { standardMetrics: standard, customMetrics: getCustomMetrics(filteredAssets) };
  }, [assets, selectedAssetType]);

  const availableAssetMetrics = useMemo(() => [...standardMetrics, ...customMetrics], [standardMetrics, customMetrics]);

  const handleAssetTypeChange = (type: AssetFilterValue) => {
    setSelectedAssetType(type);
    setSelectedMetricKey(null);
  };

  const activeMetric = availableAssetMetrics.find((m) => m.key === selectedMetricKey) ?? availableAssetMetrics[0] ?? null;

  // Build treemap nodes
  const { treemapData, excludedCount, totalCount } = useMemo(() => {
    if (!activeMetric) return { treemapData: [], excludedCount: 0, totalCount: 0 };
    const filtered = selectedAssetType === 'all' ? assets : assets.filter((a) => a.assetType === selectedAssetType);
    const nodes: TreemapNode[] = [];
    let excluded = 0;

    for (const asset of filtered) {
      const rec = asset as unknown as Record<string, unknown>;
      const comparison = comparisonMap.get(asset._id);
      const co2e = comparison?.totalCo2e ?? 0;
      const denom = activeMetric.getDenominator(rec);

      if (denom == null || denom <= 0) {
        excluded++;
        continue;
      }

      nodes.push({
        name: asset.name,
        size: denom,
        intensity: co2e / denom,
        totalCo2e: co2e,
        denominatorValue: denom,
        id: asset._id,
      });
    }

    return { treemapData: nodes, excludedCount: excluded, totalCount: filtered.length };
  }, [assets, comparisonMap, selectedAssetType, activeMetric]);

  if (isLoading) {
    return <div className='tw:flex tw:items-center tw:justify-center tw:py-10'><Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' /></div>;
  }

  if (assets.length === 0) {
    return <EmptyStateCard title='Intensity Treemap' message='No assets found. Create assets to see the intensity treemap.' />;
  }

  if (!activeMetric) {
    return (
      <div className='tw:space-y-4'>
        <AssetTypeFilter value={selectedAssetType} onChange={handleAssetTypeChange} disabledTypes={disabledTypes} />
        <EmptyStateCard
          title='Intensity Treemap'
          message='No assets have intensity denominators (floor area, employees, vehicles, or operating hours) configured.'
        />
      </div>
    );
  }

  return (
    <div className='tw:space-y-4'>
      <AssetTypeFilter value={selectedAssetType} onChange={handleAssetTypeChange} disabledTypes={disabledTypes} />
      <IntensityTreemap
        data={treemapData}
        metric={activeMetric}
        headerExtra={<MetricSelector standardMetrics={standardMetrics} customMetrics={customMetrics} value={activeMetric.key} onChange={setSelectedMetricKey} />}
      />
      {excludedCount > 0 && (
        <p className='tw:text-xs tw:text-muted-foreground'>
          Showing {treemapData.length} of {totalCount} assets. {excludedCount} excluded (missing {activeMetric.label} data).
        </p>
      )}
    </div>
  );
};

// ============================================================================
// Shared: Metric Selector
// ============================================================================

interface MetricOption { key: string; label: string }

const MetricSelector = ({ standardMetrics, customMetrics, value, onChange }: {
  standardMetrics: MetricOption[];
  customMetrics: MetricOption[];
  value: string;
  onChange: (key: string) => void;
}) => {
  const isCustomSelected = value.startsWith('custom:');
  const customLabel = customMetrics.find((m) => m.key === value)?.label;
  const standardTabs = standardMetrics.map((m) => ({ key: m.key, label: m.label }));

  return (
    <div className='tw:flex tw:items-center tw:gap-1 tw:rounded-lg tw:bg-muted/50 tw:p-1'>
      <TabNav
        size='xs'
        variant='toggle'
        className='tw:!bg-transparent tw:!p-0'
        tabs={standardTabs}
        activeTab={isCustomSelected ? '' : value}
        onChange={onChange}
      />
      {customMetrics.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'tw:appearance-none tw:inline-flex tw:items-center tw:gap-1 tw:rounded-md tw:border-0 tw:px-3 tw:py-1 tw:text-xs tw:font-medium tw:transition-all tw:cursor-pointer',
              isCustomSelected
                ? 'tw:bg-background tw:text-blue-600 tw:shadow-sm'
                : 'tw:bg-transparent tw:text-muted-foreground tw:hover:text-foreground',
            )}
          >
            {isCustomSelected ? customLabel : 'Custom'}
            <ChevronDown className='tw:size-3 tw:opacity-60' />
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {customMetrics.map((m) => (
              <DropdownMenuItem key={m.key} onClick={() => onChange(m.key)}>
                {m.label}
                {value === m.key && <Check className='tw:ml-auto tw:size-3.5 tw:text-blue-600' />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
